import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import Header from 'components/Header/Header';
import { IHeader } from 'components/Header';
import { DeleteDialog } from 'components/DeleteDialog';
import { deleteSampleUlr } from 'actions/LaboratoryActions';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { IReducer } from 'Reducers/IReducer';
import { LaboratoryService } from 'services/LaboratoryService';
import { useNavigate } from 'react-router-dom';
import { IUlrManagerProps, IUlrManagerRow } from './IUlrManager';

function _UlrManager(props: IUlrManagerProps) {
  const navigate = useNavigate();
  const laboratoryId = props.laboratory?.details?._id || '';
  const pageSize = 20;
  const [loading, setLoading] = useState(true);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [rows, setRows] = useState<IUlrManagerRow[]>([]);
  const [selectedSampleId, setSelectedSampleId] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [selectedCount, setSelectedCount] = useState(0);

  const breadcrumbsList: IHeader['breadcrumbsList'] = [
    {
      label: 'Dashboard',
      onClick: () => navigate('/'),
    },
    {
      label: 'ULR Manager',
      onClick: () => navigate('/laboratory/ulr-manager'),
    },
  ];

  const selectedRow = rows.find((row) => row._id === selectedSampleId) || rows.find((row) => row.isSelected);
  const lastAffectedRow = [...rows].reverse().find((row) => row.isSelected);

  const deletePreviewText = useMemo(() => {
    if (!selectedRow || !lastAffectedRow) return 'Are you sure you want to delete the selected ULR range?';
    return `Are you sure? ULR ${selectedRow.ulr || 'N/A'} to ${lastAffectedRow.ulr || 'N/A'} will be deleted.`;
  }, [selectedRow, lastAffectedRow]);

  const loadRows = async (targetPage: number = page, targetSelectedSampleId: string = selectedSampleId) => {
    setLoading(true);
    try {
      const response = await LaboratoryService.getSampleUlrManager(laboratoryId, {
        page: targetPage,
        size: pageSize,
        selectedSampleId: targetSelectedSampleId || undefined,
      });

      const list = Array.isArray(response?.data?.samples) ? response.data.samples : [];
      const nextRows = list.map((item: any) => ({
          _id: item._id,
          ulr: item.ulr,
          sampleId: item.sampleId,
          name: item.name,
          isSelected: Boolean(item.isSelected),
        }));

      setRows(nextRows);
      setSelectedSampleId(String(response?.data?.selectedSampleId || targetSelectedSampleId || ''));
      setSelectedCount(Number(response?.data?.selectedCount) || 0);
      setPage(targetPage);
      setTotalPages(Number(response?.data?.totalPages) || 1);
      setTotal(Number(response?.data?.total) || 0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (laboratoryId) {
      loadRows(1, selectedSampleId);
    }
    // eslint-disable-next-line
  }, [laboratoryId]);

  return (
    <Box>
      <Header breadcrumbsList={breadcrumbsList} title="ULR Manager" />
      <Box p={2} pt={10}>
        <Typography variant="body2" color="text.secondary" mb={2}>
          Select a ULR. The selected ULR and all lower rows will be prepared for deletion.
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={2}>
          Showing up to 20 records per page. Total records: {total}
        </Typography>
        {selectedSampleId ? (
          <Typography variant="body2" color="text.secondary" mb={2}>
            Selected rows across all pages: {selectedCount}
          </Typography>
        ) : null}

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell width={60}></TableCell>
                <TableCell width={140}>ULR</TableCell>
                <TableCell width={220}>Sample ID</TableCell>
                <TableCell>Sample Name</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <Box display="flex" justifyContent="center" p={2}>
                      <CircularProgress size={28} />
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row, index) => {
                  const isSelected = Boolean(row.isSelected);
                  const isAffected = Boolean(row.isSelected);
                  const hideCheckbox = selectedSampleId ? isAffected && row._id !== selectedSampleId : false;

                  return (
                    <TableRow
                      key={row._id || index}
                      hover
                      selected={isSelected}
                      sx={{ cursor: hideCheckbox ? 'default' : 'pointer' }}
                      onClick={() => {
                        if (hideCheckbox) return;
                        const nextSelectedSampleId = selectedSampleId === row._id ? '' : row._id || '';
                        setSelectedSampleId(nextSelectedSampleId);
                        loadRows(page, nextSelectedSampleId);
                      }}
                    >
                      <TableCell padding="checkbox">
                        {!hideCheckbox ? <Checkbox checked={isSelected} /> : null}
                      </TableCell>
                      <TableCell
                        sx={{
                          textDecoration: isAffected ? 'line-through' : 'none',
                          color: isAffected ? 'error.main' : 'inherit',
                          fontWeight: 600,
                        }}
                      >
                        {row.ulr || 'N/A'}
                      </TableCell>
                      <TableCell
                        sx={{
                          textDecoration: isAffected ? 'line-through' : 'none',
                          color: isAffected ? 'text.disabled' : 'inherit',
                        }}
                      >
                        {row.sampleId || 'N/A'}
                      </TableCell>
                      <TableCell
                        sx={{
                          textDecoration: isAffected ? 'line-through' : 'none',
                          color: isAffected ? 'text.disabled' : 'inherit',
                        }}
                      >
                        {row.name || 'N/A'}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
              {!loading && !rows.length ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    No ULR records found.
                  </TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
        </TableContainer>

        <Box display="flex" justifyContent="space-between" alignItems="center" mt={2}>
          <Box display="flex" alignItems="center" gap={1}>
            <Button disabled={loading || page <= 1} onClick={() => loadRows(page - 1)}>
              Previous
            </Button>
            <Typography variant="body2">
              Page {page} of {totalPages || 1}
            </Typography>
            <Button disabled={loading || page >= totalPages} onClick={() => loadRows(page + 1)}>
              Next
            </Button>
          </Box>
          <Button
            color="error"
            variant="contained"
            disabled={!selectedRow || deleteLoading || loading}
            onClick={() => setDeleteModal(true)}
          >
            Confirm Delete
          </Button>
        </Box>
      </Box>

      <DeleteDialog
        title="Delete ULR"
        message={deletePreviewText}
        isVisible={deleteModal}
        isLoading={deleteLoading}
        onClose={() => {
          if (!deleteLoading) setDeleteModal(false);
        }}
        onAction={async () => {
          if (!selectedRow?._id) return;
          setDeleteLoading(true);
          try {
            await props.deleteSampleUlr(laboratoryId, selectedRow._id);
            setDeleteModal(false);
            const nextPage = rows.length === 1 && page > 1 ? page - 1 : page;
            await loadRows(nextPage);
          } finally {
            setDeleteLoading(false);
          }
        }}
      />
    </Box>
  );
}

const mapStateToProps = (state: IReducer) => ({
  laboratory: state.laboratory.current,
});

function mapDispatchToProps(dispatch: any) {
  return {
    ...bindActionCreators({ deleteSampleUlr }, dispatch),
  };
}

export const UlrManager = connect(mapStateToProps, mapDispatchToProps)(_UlrManager);
